import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, XCircle, Eye, EyeOff, X, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { AccountType } from '../lib/supabase';

interface InvitationData {
  vendor_smart_key: string;
  vendor_name: string;
  shop_name: string;
  customer_phone: string;
  customer_country_code: string;
  customer_address: string;
  expires_at: string;
}

interface FormData {
  username: string;
  password: string;
  confirmPassword: string;
  dateOfBirth: string;
  address: string;
}

function InvitePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitationData, setInitationData] = useState<InvitationData | null>(null);
  const [countdown, setCountdown] = useState<string>('');

  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [agreedToPrivacyPolicy, setAgreedToPrivacyPolicy] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<FormData>({
    username: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    address: invitationData?.customer_address || '',
  });

  useEffect(() => {
    if (code) {
      fetchInvitation(code);
    } else {
      setError('رمز الدعوة مفقود');
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    if (invitationData?.customer_address) {
      setFormData(prev => ({
        ...prev,
        address: invitationData.customer_address || '',
      }));
    }
  }, [invitationData]);

  useEffect(() => {
    if (invitationData?.expires_at) {
      const updateCountdown = () => {
        const now = new Date().getTime();
        const expiry = new Date(invitationData.expires_at).getTime();
        const difference = expiry - now;

        if (difference > 0) {
          const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);
          
          setCountdown(`${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        } else {
          setCountdown('منتهية الصلاحية');
        }
      };

      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [invitationData]);

  const fetchInvitation = async (invitationCode: string) => {
    try {
      const { data, error } = await supabase
        .rpc('get_invitation_by_code', {
          p_code: invitationCode
        });

      if (error) throw error;

      if (!data.success) {
        setError(data.error || 'الدعوة غير صالحة');
        return;
      }

      setInvitationData(data.data);
    } catch (err) {
      console.error('Error fetching invitation:', err);
      setError('فشل في تحميل بيانات الدعوة');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToPrivacyPolicy) {
      setRegistrationError('يجب الموافقة على سياسة الخصوصية للمتابعة');
      return;
    }
    
    setRegistrationError(null);
    setRegistrationLoading(true);

    try {
      if (!invitationData || !code) {
        throw new Error('بيانات الدعوة غير متوفرة');
      }

      if (formData.password !== formData.confirmPassword) {
        throw new Error('كلمات المرور غير متطابقة');
      }

      if (recoveryEmail && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(recoveryEmail)) {
        throw new Error('البريد الإلكتروني غير صالح');
      }

      // Register the user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: `${invitationData.customer_phone}@ajel.life`,
        password: formData.password,
        phone: invitationData.customer_country_code + invitationData.customer_phone,
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('لم يتم إنشاء المستخدم');

      // Create profile
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          username: formData.username,
          phone: invitationData.customer_phone,
          country_code: invitationData.customer_country_code,
          date_of_birth: formData.dateOfBirth,
          address: formData.address,
          account_type: 'customer' as AccountType,
          reference_smart_key: invitationData.vendor_smart_key,
          shop_name: null,
          business_type: null,
          assistant_vendor_smart_key: null,
          assistant_status: null,
          recovery_email: recoveryEmail || null
        });

      if (profileError) {
        await supabase.auth.signOut();
        throw profileError;
      }

      // Use the invitation code
      try {
        const { data: invitationResult, error: invitationError } = await supabase
          .rpc('use_invitation', {
            p_code: code,
            p_customer_user_id: authData.user.id
          });

        if (invitationError) {
          console.error('Error using invitation:', invitationError);
        }
      } catch (invitationError) {
        console.error('Error in invitation processing:', invitationError);
      }

      navigate('/home', { replace: true });
    } catch (err) {
      console.error('Registration error:', err);
      setRegistrationError(err instanceof Error ? err.message : 'حدث خطأ أثناء التسجيل');
    } finally {
      setRegistrationLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">جاري تحميل الدعوة...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white rounded-3xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-800 mb-4">دعوة غير صالحة</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors"
          >
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  if (!invitationData) {
    return null;
  }

  const isExpired = new Date(invitationData.expires_at) <= new Date();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {isExpired ? (
              <Clock className="w-8 h-8 text-orange-600" size={32} />
            ) : (
              <CheckCircle className="w-8 h-8 text-green-600" size={32} />
            )}
          </div>
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">
            {isExpired ? 'دعوة منتهية الصلاحية' : 'دعوة للانضمام'}
          </h1>
          <p className="text-gray-600">تطبيق آجل لإدارة الحسابات</p>
        </div>

        {!isExpired && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-center mb-3">
              <span className="text-2xl">🎉</span>
            </div>
            <p className="text-blue-700 text-sm mb-2 text-center">
              <span className="font-medium">{invitationData.vendor_name}</span>
              {invitationData.shop_name && (
                <span> من {invitationData.shop_name}</span>
              )}
              <br />
              يدعوك للانضمام إلى تطبيق آجل
            </p>
            <div className="bg-blue-100 rounded-lg p-3 mb-3">
              <p className="text-blue-800 text-xs font-medium mb-1">رقم الهاتف:</p>
              <p className="text-blue-900 font-semibold">
                {invitationData.customer_country_code} {invitationData.customer_phone}
              </p>
            </div>
            {invitationData.customer_address && (
              <div className="bg-blue-100 rounded-lg p-3 mb-3">
                <p className="text-blue-800 text-xs font-medium mb-1">العنوان:</p>
                <p className="text-blue-900 text-sm">{invitationData.customer_address}</p>
              </div>
            )}
            <div className="text-center">
              <p className="text-blue-600 text-xs mb-1">صالحة لمدة 48 ساعة:</p>
              <p className="text-blue-800 font-mono font-bold">{countdown}</p>
            </div>
          </div>
        )}

        {isExpired ? (
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-orange-700 text-sm text-center">
                انتهت صلاحية هذه الدعوة. يرجى التواصل مع {invitationData.vendor_name} للحصول على دعوة جديدة.
              </p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors"
            >
              العودة للرئيسية
            </button>
          </div>
        ) : (
          showRegistrationForm ? (
            // Registration Form
            <form onSubmit={handleRegistration} className="space-y-4">
              {registrationError && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {registrationError}
                </div>
              )}
              
              <div>
                <input
                  type="text"
                  name="username"
                  placeholder="اسم المستخدم"
                  value={formData.username}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-700 mb-1 font-medium">رقم الهاتف</p>
                <p className="text-sm font-medium">
                  {invitationData.customer_country_code} {invitationData.customer_phone}
                </p>
              </div>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="كلمة المرور"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-right pl-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="تأكيد كلمة المرور"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-right pl-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <div>
                <input
                  type="date"
                  name="dateOfBirth"
                  placeholder="تاريخ الميلاد"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <input
                  type="text"
                  name="address"
                  placeholder="العنوان"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <input
                  type="email"
                  placeholder="البريد الإلكتروني لاستعادة كلمة المرور (اختياري)"
                  value={recoveryEmail}
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  سيتم ربط حسابك تلقائيا بهذا المتجر عند اكمال التسجيل, اذا كنت تتعامل مع اكثر من متجر, اطلب من المتاجر الاخرى اضافتك عن طريق رقم هاتفك
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="privacyPolicy"
                  checked={agreedToPrivacyPolicy}
                  onChange={(e) => setAgreedToPrivacyPolicy(e.target.checked)}
                  className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="privacyPolicy" className="text-sm text-gray-600">
                  أوافق على{' '}
                  <button
                    type="button"
                    onClick={() => setShowPrivacyPolicy(true)}
                    className="text-blue-500 hover:text-blue-600"
                  >
                    سياسة الخصوصية
                  </button>
                </label>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowRegistrationForm(false)}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  رجوع
                </button>
                <button
                  type="submit"
                  disabled={registrationLoading || !agreedToPrivacyPolicy}
                  className="flex-1 py-3 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {registrationLoading ? 'جاري إنشاء الحساب...' : 'إكمال التسجيل'}
                </button>
              </div>
              
              <div className="text-center text-sm text-gray-500 pt-2">
                <button
                  onClick={() => navigate('/login')}
                  className="text-blue-500 hover:text-blue-600"
                >
                  لديّ حساب بالفعل - تسجيل الدخول
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <button
                onClick={() => setShowRegistrationForm(true)}
                className="w-full py-3 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors flex items-center justify-center space-x-2"
              >
                <User size={18} />
                <span>قبول الدعوة والتسجيل</span>
              </button>
              
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
              >
                لديّ حساب بالفعل - تسجيل الدخول
              </button>
            </div>
          )
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            رمز الدعوة: <span className="font-mono font-bold">{code}</span>
          </p>
        </div>
      </div>
      
      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-2xl rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">سياسة الخصوصية</h2>
              <button 
                onClick={() => setShowPrivacyPolicy(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="prose prose-sm max-w-none">
              <p className="text-sm text-gray-600 mb-4">تاريخ السريان: 01/02/2025</p>

              <p className="mb-4">
                Ajel.life ("نحن" أو "لنا") ملتزمون بحماية خصوصيتك. توضح سياسة الخصوصية هذه كيفية جمع واستخدام والكشف عن وحماية معلوماتك عند استخدام تطبيق الويب الخاص بنا ("التطبيق").
              </p>

              <h3 className="text-lg font-semibold mb-2">المعلومات التي نجمعها</h3>
              <p className="mb-4">قد نجمع المعلومات التالية منك:</p>
              <ul className="list-disc list-inside mb-4">
                <li>عنوان البريد الإلكتروني</li>
                <li>رقم الهاتف المحمول</li>
                <li>اسم المستخدم</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">كيف نستخدم معلوماتك</h3>
              <p className="mb-2">نستخدم المعلومات التي نجمعها للأغراض التالية:</p>
              <ul className="list-disc list-inside mb-4">
                <li>إنشاء وإدارة حسابات المستخدمين</li>
                <li>التواصل معك، بما في ذلك إرسال التحديثات والإشعارات</li>
                <li>تحسين خدماتنا وتجربة المستخدم</li>
                <li>تقديم دعم العملاء</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">مشاركة المعلومات</h3>
              <p className="mb-2">
                نحن لا نبيع أو نؤجر أو نشارك معلوماتك الشخصية مع أطراف ثالثة إلا في الحالات التالية:
              </p>
              <ul className="list-disc list-inside mb-4">
                <li>بموافقتك</li>
                <li>لأسباب قانونية، مثل الامتثال للالتزامات القانونية أو الاستجابة للطلبات القانونية أو حماية حقوقنا</li>
                <li>مع مقدمي الخدمات الذين يقدمون خدمات نيابة عنا بموجب اتفاقيات سرية صارمة</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">أمن البيانات</h3>
              <p className="mb-4">
                نقوم بتنفيذ تدابير تقنية وتنظيمية مناسبة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو الاستخدام أو الكشف أو التعديل أو التدمير.
              </p>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowPrivacyPolicy(false)}
                  className="px-4 py-2 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvitePage;